import { Controller } from "@hotwired/stimulus";

interface OwnerSelectEvent extends CustomEvent {
  detail: { item: { label: string; value: string } };
}

export default class PersonalCardFormController extends Controller<HTMLElement> {
  static override targets = ["card", "loading"];
  static override values = { detailsUrl: String };

  declare readonly cardTarget: HTMLElement;
  declare readonly detailsUrlValue: string;
  declare readonly hasLoadingTarget: boolean;
  declare readonly loadingTarget: HTMLElement;

  private pendingLookups = 0;
  private currentLookup: AbortController | null = null;

  override connect(): void {
    const owner = this.element.querySelector<HTMLInputElement>(
      'input[name="owner"]',
    )?.value;
    if (owner) {
      void this.fillDetails(owner, { overwrite: false });
    }
  }

  updateOwner(event: OwnerSelectEvent): void {
    const { label, value } = event.detail.item;
    const name = this.cardTarget.querySelector(
      '[data-card-field="cardholder-name"]',
    );
    if (name) {
      name.textContent = label.toUpperCase();
    }
    const confirmation = this.element.querySelector<HTMLInputElement>(
      'input[name="is_legal_name_confirmed"]',
    );
    if (confirmation) {
      confirmation.checked = false;
    }
    void this.fillDetails(value, { overwrite: true });
  }

  private async fillDetails(
    owner: string,
    { overwrite }: { overwrite: boolean },
  ): Promise<void> {
    this.currentLookup?.abort();
    const lookup = new AbortController();
    this.currentLookup = lookup;
    this.setLookingUp(1);
    try {
      const response = await fetch(
        `${this.detailsUrlValue}?owner=${encodeURIComponent(owner)}`,
        { signal: lookup.signal },
      );
      if (!response.ok) {
        return;
      }
      const details = (await response.json()) as Record<
        string,
        string | boolean | null
      >;
      for (const [name, value] of Object.entries(details)) {
        if (typeof value === "boolean") {
          continue;
        }
        const input = this.element.querySelector<
          HTMLInputElement | HTMLSelectElement
        >(`[name="${name}"]`);
        if (input && (overwrite || !input.value)) {
          input.value = value ?? "";
        }
      }
      const phone = this.element.querySelector<HTMLInputElement>(
        'input[name="phone_number"]',
      );
      if (phone) {
        phone.readOnly = details.is_phone_verified === true;
      }
    } catch {
      // Prefilling is best effort; the user can type the details.
    } finally {
      this.setLookingUp(-1);
    }
  }

  private setLookingUp(delta: number): void {
    this.pendingLookups += delta;
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.toggle("hidden", this.pendingLookups === 0);
    }
  }
}
